/**
 * React Router v7 Routes Configuration
 *
 * Every page route of the app. The HTML document comes from src/index.html (filled in by
 * entry.server.tsx); Root (root.tsx) only wraps the routes in the shared providers and <Outlet />.
 *
 * Route structure:
 * {
 *   path: '/path',
 *   lazy: async () => import('./pages/PageName/route'), // For lazy-loaded routes
 *   // OR
 *   loader: async ({context, request, params}) => {...},
 *   Component: StaticComponent // For routes that should be in the initial bundle
 * }
 */
import type {LoaderFunctionArgs, RouteObject} from 'react-router-dom'
import {useLoaderData} from 'react-router-dom'

import {type NavigationSkeletonHandle, NavigationSkeletonOutlet} from './components/NavigationSkeleton'
import {appName, isCoches, NEWS_CATEGORY_PATHS, NEWS_DETAIL_REGEX, ROUTES} from './config/index.js'
import {UserLayout} from './layouts/UserLayout'
import MyAdsSkeleton from './pages/AdManagement/MyAds/Loading.js'
import PublishMyAdWithPlateSkeleton from './pages/AdPublisher/PublishMyAdWithPlate/Loading.js'
// Eager imports kept ONLY for routes used by catch-all wrappers below
// (catchAllLoader/catchNewsLoader need direct access to these loaders/components).
// Every other route is lazy-loaded — see `lazy:` entries in `routes` to keep the
// initial CSS bundle small.
import {Component as AdsListComponent, loader as adsListLoader} from './pages/AdsList/route'
import AdsListSkeleton from './pages/AdsList/Skeleton.js'
import AdsListSubscriptionSkeleton from './pages/AdsListSubscription/Skeleton.js'
// Detail page with feature-flagged variants (ff_web_buyer_ad_detail_revamp)
// Styles are conditionally loaded in Detail/index.js based on the feature flag
import {Component as AdDetailComponent, loader as adDetailLoader} from './pages/Detail/route'
import AdDetailSkeleton from './pages/Detail/Skeleton.js'
import {Component as NewsDetailComponent, loader as newsDetailLoader} from './pages/News/Detail/route'
import {Skeleton as NewsDetailSkeleton} from './pages/News/Detail/Skeleton.js'
import {Component as NewsListComponent, loader as newsListLoader} from './pages/News/List/route'
import ReservationInitSkeleton from './pages/Reservation/Init/Skeleton.js'
import StaticSkeleton from './pages/Static/Skeleton.js'
import TechnicalDetailsComparatorSkeleton from './pages/TechnicalDetails/Comparator/Skeleton.js'
import UserTakeOutSkeleton from './pages/User/TakeOut/Skeleton.js'
import Root from './root'

// Ad Detail regex patterns from legacy system
const ADS_DETAIL_COCHES_REGEX = /\/.*-(?<adId>\d{4,9})-[a-z]{2,4}(vo|vn)\.aspx(\?.*)?$/
const ADS_DETAIL_COCHES_SUBSCRIPTION_REGEX = /\/renting\/.*-(?<adId>\d{4,9})-vsre(\?.*)?$/
const ADS_DETAIL_MOTOS_REGEX = /\/(ocasion|nuevas)\/.*-(?<adId>\d{5,8})\.htm(\?.*)?$/
const ADS_DETAIL_PREVIEW_REGEX = /preview\/(?<adId>[a-zA-Z0-9]{1,})/

// Check if URL matches any Ad Detail pattern
export function isAdDetailUrl(pathname: string): boolean {
  return (
    ADS_DETAIL_COCHES_REGEX.test(pathname) ||
    ADS_DETAIL_COCHES_SUBSCRIPTION_REGEX.test(pathname) ||
    ADS_DETAIL_MOTOS_REGEX.test(pathname) ||
    ADS_DETAIL_PREVIEW_REGEX.test(pathname)
  )
}

// Wrapper loader that detects Ad Detail vs AdsList
async function catchAllLoader(args: LoaderFunctionArgs) {
  const url = new URL(args.request.url)
  const pathname = url.pathname

  // Check if this is an Ad Detail page
  if (isAdDetailUrl(pathname)) {
    const data = await adDetailLoader(args)
    // A redirect (or any Response) must be returned untouched. Spreading it yields `{}`
    // — Response has no enumerable own properties — which silently turned a 302 into a
    // 200 rendering an empty page. entry.server.tsx already handles a Response from
    // `handler.query()`; it just never saw one through this wrapper.
    if (data instanceof Response) return data
    return {isAdDetail: true, ...data}
  }

  // Otherwise, it's AdsList
  const data = await adsListLoader(args)
  if (data instanceof Response) return data
  return {isAdDetail: false, ...data}
}

// Wrapper component that renders Ad Detail or AdsList
function CatchAllComponent() {
  const data = useLoaderData()

  if (data?.isAdDetail) {
    // Use adId as key to force re-render when navigating between different ads
    const adId = data?.ad?.id || data?.adId
    return <AdDetailComponent key={adId} />
  }

  return <AdsListComponent />
}

// Wrapper loader that detects News Detail vs News List under /noticias/:param
function isNewsDetailUrl(pathname: string, param: string): boolean {
  const categories = NEWS_CATEGORY_PATHS[appName] || []
  if (categories.includes(param)) return false
  return NEWS_DETAIL_REGEX[appName].test(pathname)
}

async function catchNewsLoader(args: LoaderFunctionArgs) {
  const url = new URL(args.request.url)
  const param = args.params.param || ''
  if (isNewsDetailUrl(url.pathname, param)) {
    const data = await newsDetailLoader(args)
    // Same as catchAllLoader: pass a Response (redirect) straight through instead of
    // spreading it away.
    if (data instanceof Response) return data
    return {isNewsDetail: true, ...data}
  }
  const data = await newsListLoader({...args, params: {...args.params, category: param}})
  if (data instanceof Response) return data
  return {isNewsDetail: false, ...data}
}

function CatchNewsComponent() {
  const data = useLoaderData()
  if (data?.isNewsDetail) {
    return <NewsDetailComponent />
  }
  return <NewsListComponent />
}

/**
 * Route revalidation policy — RR7's equivalent of the legacy `keepInitialProps`
 * flag from `@s-ui/react-initial-props`.
 *
 * By default RR7 re-runs the loader on every client-side navigation, including
 * `navigate(sameUrl, {replace: true})` calls that components use to sync the
 * URL with UI state (e.g. `packages/ui/.../modal-with-url-state.js` normalises
 * the hash on mount — same pathname + search, but RR7 still revalidates and
 * triggers a fresh `/search/listing` fetch).
 *
 * We only care about revalidating when the underlying data changes — path or
 * search. Hash-only or fully-identical URL navigations skip the loader.
 * Filter changes still refetch because they mutate `search`.
 */
function dataOnlyShouldRevalidate({currentUrl, nextUrl}: {currentUrl: URL; nextUrl: URL}) {
  return currentUrl.pathname !== nextUrl.pathname || currentUrl.search !== nextUrl.search
}

// Public step URLs of the vehicle configurator (/configurador/<step>/), as in the original app's config.
const CONFIGURATOR_STEPS = ['combustible', 'acabados', 'motor', 'color', 'formulario', 'resultado']

// What each page showed while its legacy `getInitialProps` ran on the client (`Page.renderLoading`),
// rendered by NavigationSkeletonOutlet while the route loader runs. `keepMounted` mirrors the
// legacy static of the same name: navigating within the same page shows no skeleton.
const withNavigationSkeleton = (navigationSkeleton: NavigationSkeletonHandle) => ({handle: {navigationSkeleton}})
const AD_DETAIL_NAVIGATION_SKELETON = {Skeleton: AdDetailSkeleton}
const ADS_LIST_NAVIGATION_SKELETON = {Skeleton: AdsListSkeleton, keepMounted: true}
const NEWS_DETAIL_NAVIGATION_SKELETON = {Skeleton: NewsDetailSkeleton}
const adDetailSkeleton = withNavigationSkeleton(AD_DETAIL_NAVIGATION_SKELETON)
const newsDetailSkeleton = withNavigationSkeleton(NEWS_DETAIL_NAVIGATION_SKELETON)
const staticSkeleton = withNavigationSkeleton({Skeleton: StaticSkeleton})
const reservationInitSkeleton = withNavigationSkeleton({Skeleton: ReservationInitSkeleton})
const comparatorSkeleton = withNavigationSkeleton({Skeleton: TechnicalDetailsComparatorSkeleton})
const adsListSubscriptionSkeleton = withNavigationSkeleton({Skeleton: AdsListSubscriptionSkeleton, keepMounted: true})

function RootWithNavigationSkeleton() {
  return <Root outlet={<NavigationSkeletonOutlet routes={appRoutes} />} />
}

const appRoutes: RouteObject[] = [
  {
    // Root route - renders full HTML document
    path: '/',
    Component: RootWithNavigationSkeleton,
    children: [
      // Home page
      {
        index: true,
        lazy: async () => import('./pages/Home/route')
      },
      // ==============================================
      // USER PAGES (Migrated - TASK-1)
      // ==============================================
      // All user-related pages wrapped in UserLayout for authentication
      {
        path: 'user',
        Component: UserLayout,
        children: [
          {path: 'profile', lazy: async () => import('./pages/User/Profile/route')},
          {path: 'profile/edit', lazy: async () => import('./pages/User/ProfileEdit/route')},
          {path: 'profile/complete/purchase', lazy: async () => import('./pages/User/ProfileComplete/route')},
          {path: 'favorites', lazy: async () => import('./pages/User/Favorites/route')},
          {path: 'favorites/:section', lazy: async () => import('./pages/User/Favorites/route')},
          {path: 'alerts', lazy: async () => import('./pages/User/Alerts/route')},
          {path: 'alerts/:alertId', lazy: async () => import('./pages/User/AlertsMatches/route')},
          {path: 'alert/:userId/:alertId', lazy: async () => import('./pages/User/Alert/route')},
          {path: 'discounts', lazy: async () => import('./pages/User/Discounts/route')},
          {path: 'phone/verification', lazy: async () => import('./pages/User/PhoneVerification/route')},
          {path: 'messages/*', lazy: async () => import('./pages/User/Messages/route')},
          {
            path: 'takeout',
            lazy: async () => import('./pages/User/TakeOut/route'),
            ...withNavigationSkeleton({Skeleton: UserTakeOutSkeleton})
          }
        ]
      },

      // Unsubscribe routes (outside UserLayout, don't require auth)
      {
        path: 'cancelar-suscripcion-favorito/:userId/:adId',
        lazy: async () => import('./pages/User/UnsubscribeFavorite/route')
      },
      {path: 'cancelar-suscripcion-emails', lazy: async () => import('./pages/User/UnsubscribeEmails/route')},

      // Alerts unsubscribe (Coches-specific)
      {
        path: 'coches/alerts/unsubscribe/:userId/savedsearch/:alertId',
        lazy: async () => import('./pages/User/Alerts/Unsubscribe/route')
      },

      // ==============================================
      // PUBLISH FLOW (Migrated - TASK-2)
      // ==============================================
      // Sell/Publish vehicle flow
      {path: 'publicar-mi-anuncio', lazy: async () => import('./pages/PublishMyAd/route')},
      {path: 'editar-mi-anuncio', lazy: async () => import('./pages/PublishMyAd/route')},
      {
        path: 'vender-mi-coche-con-matricula',
        lazy: async () => import('./pages/AdPublisher/PublishMyAdWithPlate/route'),
        ...withNavigationSkeleton({Skeleton: PublishMyAdWithPlateSkeleton})
      },
      {path: 'vender-mi-coche/:location?', lazy: async () => import('./pages/PublishYourAd/route')},
      {path: 'vender-mi-moto/:location?', lazy: async () => import('./pages/PublishYourAd/route')},

      // Instant Offer / C2B (Sell to dealer)
      {path: 'vende-a-concesionario/:adId?', lazy: async () => import('./pages/InstantOffer/route')},
      {path: 'vende-a-un-profesional/:adId?', lazy: async () => import('./pages/InstantOffer/route')},
      {
        path: 'vende-a-concesionario-siguiente-cita',
        lazy: async () => import('./pages/InstantOfferNextAppointment/route')
      },
      {path: 'vender-mi-coche-a-un-concesionario', lazy: async () => import('./pages/InstantOfferStandalone/route')},
      {path: 'vender-mi-moto-a-un-profesional', lazy: async () => import('./pages/InstantOfferStandalone/route')},

      // Ad Publisher (category selector)
      {
        path: 'publicar-mi-anuncio/categoria',
        lazy: async () => import('./pages/AdPublisher/PublishMyAdCategory/route')
      },

      // Ad Management (seller's own ads)
      {
        path: 'mis-anuncios',
        lazy: async () => import('./pages/AdManagement/MyAds/route'),
        ...withNavigationSkeleton({Skeleton: MyAdsSkeleton})
      },
      // ==============================================
      // COMPARATOR (Ads Comparator)
      // ==============================================
      {path: 'comparador', lazy: async () => import('./pages/Comparator/route')},

      // Coches only: motos answers /assistant/ with the news-tag redirect in production.
      ...(isCoches ? [{path: 'assistant', lazy: async () => import('./pages/Net/route')}] : []),

      // ==============================================
      // CORE PAGES (AdsListSubscription) + DEALERS
      // ==============================================
      // Saved searches / subscription listing
      {
        path: 'mis-busquedas/*',
        lazy: async () => import('./pages/AdsListSubscription/route'),
        ...adsListSubscriptionSkeleton
      },
      {
        path: 'mis-busquedas-motos/*',
        lazy: async () => import('./pages/AdsListSubscription/route'),
        ...adsListSubscriptionSkeleton
      },

      // Dealers list
      {path: 'concesionarios/*', lazy: async () => import('./pages/DealersList/route')},

      // ==============================================
      // AUTHENTICATION
      // ==============================================
      {path: 'login', lazy: async () => import('./pages/Login/route')},

      // ==============================================
      // DETAIL (legacy URL), RATING, RECOMMENDATIONS
      // ==============================================
      // Semantic ad detail URLs are matched by the catch-all loader at the end of this file.
      {path: ROUTES.DETAIL.AD, lazy: async () => import('./pages/Detail/detailLegacy.route')},
      {path: 'rating/:tradeId', lazy: async () => import('./pages/Rating/route')},
      {path: 'recommendations', lazy: async () => import('./pages/Recommendations/route')},

      // Insurance
      {path: 'seguros', lazy: async () => import('./pages/Insurance/route')},

      // Transaction routes — Buyer flow
      {
        path: 'transaction/buyer/check-costs-list',
        lazy: async () => import('./pages/Transaction/BuyerCheckCostsList/route')
      },
      {path: 'transaction/buyer/id', lazy: async () => import('./pages/Transaction/BuyerId/route')},
      {
        path: 'transaction/buyer/id-upload-back',
        lazy: async () => import('./pages/Transaction/BuyerIdUploadBack/route')
      },
      {
        path: 'transaction/buyer/id-upload-front',
        lazy: async () => import('./pages/Transaction/BuyerIdUploadFront/route')
      },
      {path: 'transaction/buyer/identity', lazy: async () => import('./pages/Transaction/BuyerIdentity/route')},
      {path: 'transaction/buyer/init-payment', lazy: async () => import('./pages/Transaction/BuyerInitPayment/route')},
      // payment-fail + payment-success share a StatusRedirect component that reads the raw pathname
      {
        path: 'transaction/buyer/payment-fail',
        lazy: async () => import('./pages/Transaction/BuyerPaymentStatusRedirect/route')
      },
      {
        path: 'transaction/buyer/payment-success',
        lazy: async () => import('./pages/Transaction/BuyerPaymentStatusRedirect/route')
      },
      {path: 'transaction/buyer/profession', lazy: async () => import('./pages/Transaction/BuyerProfession/route')},
      {path: 'transaction/buyer/passcode', lazy: async () => import('./pages/Transaction/BuyerSetPasscode/route')},

      // Transaction routes — Seller flow
      {path: 'transaction/seller/account', lazy: async () => import('./pages/Transaction/SellerAccount/route')},
      {path: 'transaction/seller/id', lazy: async () => import('./pages/Transaction/SellerId/route')},
      {path: 'transaction/seller/identity', lazy: async () => import('./pages/Transaction/SellerIdentity/route')},
      {
        path: 'transaction/seller/id-upload-back',
        lazy: async () => import('./pages/Transaction/SellerIdUploadBack/route')
      },
      {
        path: 'transaction/seller/id-upload-front',
        lazy: async () => import('./pages/Transaction/SellerIdUploadFront/route')
      },

      // Transaction routes — Price + Terms
      {path: 'transaction/price/confirm', lazy: async () => import('./pages/Transaction/PriceConfirm/route')},
      {path: 'transaction/price', lazy: async () => import('./pages/Transaction/PriceInform/route')},
      {path: 'transaction/terms', lazy: async () => import('./pages/Transaction/Terms/route')},

      // Reservation
      // Messaging deep links, coches only: motos answers them with the legacy .NET redirect in production.
      // (/c2b/* is not served by this app in production either.)
      ...(isCoches
        ? ['reservation/seller', 'reservation/buyer', 'transaction/seller', 'transaction/buyer'].map(path => ({
            path,
            lazy: async () => import('./pages/MessagingActionProxy/route')
          }))
        : []),
      {
        path: 'reservation/init',
        lazy: async () => import('./pages/Reservation/Init/route'),
        ...reservationInitSkeleton
      },
      // `reservation/email` is deprecated but still linked from e-mail templates already sent
      {
        path: 'reservation/email',
        lazy: async () => import('./pages/Reservation/Init/route'),
        ...reservationInitSkeleton
      },
      {path: 'reservation/phone/required', lazy: async () => import('./pages/Reservation/RequirePhoneNumber/route')},
      {path: 'reservation/payment', lazy: async () => import('./pages/Reservation/Payment/route')},
      {path: 'reservation/payment/error', lazy: async () => import('./pages/Reservation/Payment/Error/route')},
      {
        path: 'reservation/payment/success',
        lazy: async () => import('./pages/Reservation/Payment/StatusRedirect/route')
      },
      {path: 'reservation/refund', lazy: async () => import('./pages/Reservation/Refund/route')},
      {path: 'reservation/refund/reason', lazy: async () => import('./pages/Reservation/Refund/Reason/route')},
      {path: 'reservation/refund/error', lazy: async () => import('./pages/Reservation/Refund/Error/route')},

      // Static pages (markdown-driven — the Static route resolves pageName from pathname)
      {path: 'condiciones-de-uso-reserva', lazy: async () => import('./pages/Static/route'), ...staticSkeleton},
      {path: 'condiciones-de-uso', lazy: async () => import('./pages/Static/route'), ...staticSkeleton},
      {path: 'transparencia', lazy: async () => import('./pages/Static/route'), ...staticSkeleton},
      {path: 'cookies-otros-partners', lazy: async () => import('./pages/Static/route'), ...staticSkeleton},

      // Landing pages (not migrated yet)
      {path: 'vender-coche-a-un-concesionario', lazy: async () => import('./pages/Landing/InstantOffer/route')},
      {path: 'vender-coche-a-un-profesional', lazy: async () => import('./pages/Landing/InstantOfferTest/route')},

      // Payment & Products
      {path: 'payment-ad', lazy: async () => import('./pages/PaymentAd/route')},
      {path: 'products', lazy: async () => import('./pages/Products/route')},

      // Preview
      {path: 'preview/:adId', loader: adDetailLoader, Component: AdDetailComponent, ...adDetailSkeleton},

      // Stock & Dealers
      {
        path: 'concesionario/:dealerSlug/:section?',
        shouldRevalidate: dataOnlyShouldRevalidate,
        lazy: async () => import('./pages/Stock/route')
      },
      {
        path: 'renting/concesionario/:dealerSlug/:section?',
        shouldRevalidate: dataOnlyShouldRevalidate,
        lazy: async () => import('./pages/Stock/route')
      },

      // Integration
      {path: 'finalizar-integracion', lazy: async () => import('./pages/EndIntegration/route')},

      // Financing & Formalities
      {path: 'financiacion', lazy: async () => import('./pages/Landing/Financing/route')},
      {path: 'tramites-compra-venta-coches', lazy: async () => import('./pages/Landing/Formalities/route')},
      {path: 'tramites-compra-venta-motos', lazy: async () => import('./pages/Landing/Formalities/route')},
      {
        path: 'guia-seguridad-compra-venta-coches',
        lazy: async () => import('./pages/Landing/FormalitiesSecurityGuidelines/route')
      },
      {
        path: 'guia-seguridad-compra-venta-motos',
        lazy: async () => import('./pages/Landing/FormalitiesSecurityGuidelines/route')
      },

      // Technical Details - routes moved to correct section below with proper loaders

      // Secure Payment
      {path: 'beneficios-vendedor-pago-seguro', lazy: async () => import('./pages/SellerSecurePaymentBenefits/route')},
      {path: 'kyc-pago-seguro', lazy: async () => import('./pages/SecurePaymentKyc/route')},

      // Makes & Models routes moved to correct section below

      // Vehicle Configurator (steps like /configurador/color/ resolve through :make by pageName)
      // Coches only: motos answers /configurador/ with the news-tag redirect in production.
      ...(isCoches
        ? [
            // Steps declared with their real path so they never reach `:make`; the page still
            // resolves the step from the pathname.
            ...CONFIGURATOR_STEPS.map(step => ({
              path: `configurador/${step}`,
              lazy: async () => import('./pages/VehicleConfigurator/route')
            })),
            {path: 'configurador/:make?/:model?', lazy: async () => import('./pages/VehicleConfigurator/route')}
          ]
        : []),

      // Vehicle Appraiser
      {path: 'tasacion-de-coches', lazy: async () => import('./pages/Landing/VehicleAppraiser/route')},

      // News routes (migrated)
      {
        path: 'preview-news-detail/:articleId',
        loader: newsDetailLoader,
        Component: NewsDetailComponent,
        ...newsDetailSkeleton
      },
      // /opiniones/* is an alternate URL that serves the same News/Detail page — mirrors
      // the legacy `NEWS_COMMENTS_PATH_PATTERN → loadNewsCommentsPage → News/Detail` wiring.
      {
        path: 'opiniones/:category/:slug/:optional1?/:optional2?/:optional3?/:optional4?',
        loader: newsDetailLoader,
        Component: NewsDetailComponent,
        ...newsDetailSkeleton
      },
      {path: 'consejos/:slug', loader: newsDetailLoader, Component: NewsDetailComponent, ...newsDetailSkeleton},
      {path: 'consejos', loader: newsListLoader, Component: NewsListComponent},
      {path: 'tendencias/:slug', loader: newsDetailLoader, Component: NewsDetailComponent, ...newsDetailSkeleton},
      {path: 'tendencias', loader: newsListLoader, Component: NewsListComponent},
      {path: 'comparativas/:slug', loader: newsDetailLoader, Component: NewsDetailComponent, ...newsDetailSkeleton},
      {path: 'videos/:slug', loader: newsDetailLoader, Component: NewsDetailComponent, ...newsDetailSkeleton},
      {path: 'noticias/t/:tag', loader: newsListLoader, Component: NewsListComponent},
      {path: 'noticias', loader: newsListLoader, Component: NewsListComponent},
      {
        path: 'noticias/:param',
        loader: catchNewsLoader,
        shouldRevalidate: dataOnlyShouldRevalidate,
        Component: CatchNewsComponent,
        ...withNavigationSkeleton(({pathname, params}) =>
          isNewsDetailUrl(pathname, params.param ?? '') ? NEWS_DETAIL_NAVIGATION_SKELETON : undefined
        )
      },

      // Checkout — one Checkout page + one CheckoutResponsePage that handles 6 result codes.
      // The response variants render different content based on the pathname (see response.js).
      {
        path: 'checkout',
        children: [
          {index: true, lazy: async () => import('./pages/Checkout/route')},
          {path: 'success', lazy: async () => import('./pages/Checkout/response-route')},
          {path: 'error', lazy: async () => import('./pages/Checkout/response-route')},
          {path: 'pending', lazy: async () => import('./pages/Checkout/response-route')},
          {path: 'refund-error', lazy: async () => import('./pages/Checkout/response-route')},
          {path: 'activation-error', lazy: async () => import('./pages/Checkout/response-route')},
          {path: 'request-error', lazy: async () => import('./pages/Checkout/response-route')}
        ]
      },

      // ==============================================
      // Technical Details (Vehicle Info)
      // ==============================================
      {path: 'fichas_tecnicas', lazy: async () => import('./pages/TechnicalDetails/route')},
      {path: 'fichas_tecnicas/buscador', lazy: async () => import('./pages/TechnicalDetails/Search/route')},
      // Coches only: the comparator reads TECHNICAL_DETAILS_COMPARATOR_SPECIFICATIONS_FIELDS,
      // which only the coches config defines. Registering it for motos too is what makes
      // motos.coches.net/fichas_tecnicas/comparador/ a 500 in production.
      ...(isCoches
        ? [
            {
              path: 'fichas_tecnicas/comparador',
              lazy: async () => import('./pages/TechnicalDetails/Comparator/route'),
              ...comparatorSkeleton
            },
            {
              path: 'fichas_tecnicas/comparador/:vehicle1Id/:vehicle2Id?',
              lazy: async () => import('./pages/TechnicalDetails/Comparator/route'),
              ...comparatorSkeleton
            }
          ]
        : []),
      // The deeper fichas_tecnicas paths are tenant-exclusive: coches addresses a vehicle by
      // bodyType/doors/version, motos straight by vehicleId. Registering both shapes for both
      // tenants makes `:versionId` swallow a motos `:vehicleId` and vice versa.
      ...(isCoches
        ? [
            {
              path: 'fichas_tecnicas/:make/:model/:bodyType/:doors/:version/:versionId/:vehicleId',
              lazy: async () => import('./pages/TechnicalDetails/Vehicle/route')
            },
            {
              path: 'fichas_tecnicas/:make/:model/:bodyType/:doors/:version/:versionId',
              lazy: async () => import('./pages/TechnicalDetails/Version/route')
            },
            {
              path: 'fichas_tecnicas/:make/:model/:bodyType/:doors/:version',
              lazy: async () => import('./pages/TechnicalDetails/Model/route')
            },
            {
              path: 'fichas_tecnicas/:make/:model/:bodyType/:doors',
              lazy: async () => import('./pages/TechnicalDetails/Model/route')
            }
          ]
        : [
            {
              path: 'fichas_tecnicas/:make/:model/:versionId/:vehicleId',
              lazy: async () => import('./pages/TechnicalDetails/Vehicle/route')
            },
            {
              path: 'fichas_tecnicas/:make/:model/:vehicleId',
              lazy: async () => import('./pages/TechnicalDetails/Vehicle/route')
            }
          ]),
      {path: 'fichas_tecnicas/:make/:model', lazy: async () => import('./pages/TechnicalDetails/Model/route')},
      {path: 'fichas_tecnicas/:make', lazy: async () => import('./pages/TechnicalDetails/Make/route')},

      // Vehicle Suggester - All routes use same loader/component, getInitialProps handles routing internally.
      // Registered for both tenants: motos has no VEHICLE_SUGGESTER_* page config (coches-only
      // feature, see packages/domain/src/config/motos/page.js), so get_page_name_page_use_case never
      // resolves a pageName for motos and getInitialProps.js 404s (see the comment there) instead of
      // crashing. Matches production, which answers /ayudame-a-elegir on motos with a 404.
      {path: 'ayudame-a-elegir', lazy: async () => import('./pages/VehicleSuggester/route')},
      {path: 'ayudame-a-elegir/tipo-de-carroceria', lazy: async () => import('./pages/VehicleSuggester/route')},
      {path: 'ayudame-a-elegir/combustible', lazy: async () => import('./pages/VehicleSuggester/route')},
      {path: 'ayudame-a-elegir/plazas', lazy: async () => import('./pages/VehicleSuggester/route')},
      {path: 'ayudame-a-elegir/marcas', lazy: async () => import('./pages/VehicleSuggester/route')},
      {path: 'ayudame-a-elegir/resultados', lazy: async () => import('./pages/VehicleSuggester/route')},

      // Model Detail
      {path: 'modelos/:make/:model', lazy: async () => import('./pages/ModelDetail/route')},

      {path: 'marcas/:make', lazy: async () => import('./pages/Make/route')},
      {path: 'marcas-concesionarios', lazy: async () => import('./pages/MakesDistributor/route')},

      // /s/ routes (sizes, interior, trunk, make, model)
      {path: 's/medidas/:make/:model', lazy: async () => import('./pages/ModelDetail/route')},
      {path: 's/interior/:make/:model', lazy: async () => import('./pages/ModelDetail/route')},
      {path: 's/maletero/:make/:model', lazy: async () => import('./pages/ModelDetail/route')},
      {path: 's/:make/:model', lazy: async () => import('./pages/ModelDetail/route')},
      {path: 's/:make', lazy: async () => import('./pages/Make/route')},
      {path: 's', lazy: async () => import('./pages/MakesDistributor/route')},

      // ==============================================
      // AdsList / Ad Detail catch-all (MUST BE LAST)
      // ==============================================
      // This catch-all handles both AdsList and Ad Detail pages
      // It uses regex detection to determine which component to render:
      // - Ad Detail: URLs matching .aspx, .htm, -vsre patterns with ad IDs
      // - AdsList: All other URLs (segunda-mano, search, filters, etc.)
      // Both provide their own Layout component, so NOT wrapped in RootLayout
      {
        path: '*',
        loader: catchAllLoader,
        shouldRevalidate: dataOnlyShouldRevalidate,
        Component: CatchAllComponent,
        ...withNavigationSkeleton(({pathname}) =>
          isAdDetailUrl(pathname) ? AD_DETAIL_NAVIGATION_SKELETON : ADS_LIST_NAVIGATION_SKELETON
        )
      }
    ]
  }
]

/**
 * Applies `dataOnlyShouldRevalidate` to every leaf route that doesn't declare its own policy,
 * so no page re-runs its loader on hash-only or identical-URL navigations (the legacy
 * `keepInitialProps` default). Parents with children (the root layout) are left alone.
 */
function withDataOnlyRevalidation(list: RouteObject[]): RouteObject[] {
  return list.map(route => {
    if (route.children) return {...route, children: withDataOnlyRevalidation(route.children)}
    return route.shouldRevalidate ? route : {...route, shouldRevalidate: dataOnlyShouldRevalidate}
  })
}

export const routes: RouteObject[] = withDataOnlyRevalidation(appRoutes)
